import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, humanBytes, formatApiError } from "../lib/api";
import {
    ArrowLeft,
    Search,
    Upload,
    Image as ImageIcon,
    Video as VideoIcon,
    FileType2,
    FileText,
    FileQuestion,
    Loader2,
    Sparkles,
    Plus,
    X,
    Check,
    Star,
    CheckCircle2,
    MessageSquare,
    Download,
    Trash2,
    Link2,
    LayoutGrid,
    List as ListIcon,
    ArrowUpDown,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import FileDetailDialog from "../components/FileDetailDialog";
import ShareDialog from "../components/ShareDialog";
import FileShareDialog from "../components/FileShareDialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PAGE_SIZE = 20;

const KIND_ICONS = {
    image: ImageIcon,
    video: VideoIcon,
    pdf: FileType2,
    text: FileText,
    other: FileQuestion,
};

export default function ProjectPage() {
    const { projectId } = useParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [project, setProject] = useState(null);
    const [files, setFiles] = useState(null);
    const [total, setTotal] = useState(0);
    const [nextOffset, setNextOffset] = useState(null);
    const [view, setView] = useState(() => localStorage.getItem("sf_files_view") || "grid");
    const [sort, setSort] = useState(() => localStorage.getItem("sf_files_sort") || "name_asc");
    const [loadingMore, setLoadingMore] = useState(false);
    const [q, setQ] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [uploading, setUploading] = useState(0);
    const [uploadProgress, setUploadProgress] = useState([]); // [{name, pct, status}]
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [bulkTag, setBulkTag] = useState("");
    const [shareFileId, setShareFileId] = useState(null);
    const [applyingTags, setApplyingTags] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkZipProgress, setBulkZipProgress] = useState(null);

    const loadFirst = useCallback(async () => {
        try {
            const [p, f] = await Promise.all([
                api.get(`/projects/${projectId}`),
                api.get(`/projects/${projectId}/files`, {
                    params: { limit: PAGE_SIZE, sort, offset: 0 },
                }),
            ]);
            setProject(p.data);
            setFiles(f.data.items || []);
            setTotal(f.data.total || 0);
            setNextOffset(f.data.next_offset ?? null);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, [projectId, sort]);

    useEffect(() => {
        loadFirst();
    }, [loadFirst]);

    // Auto-poll while files are processing — behoudt het aantal geladen items (geen reset naar 20)
    useEffect(() => {
        if (!files) return;
        const processing = files.some((f) => f.status === "processing");
        if (!processing) return;
        const t = setTimeout(async () => {
            try {
                const count = Math.min(1000, Math.max(PAGE_SIZE, files.length));
                const { data } = await api.get(`/projects/${projectId}/files`, {
                    params: { limit: count, sort, offset: 0 },
                });
                setFiles(data.items || []);
                setTotal(data.total || 0);
                setNextOffset(data.next_offset ?? null);
            } catch {
                // stil: volgende poll probeert opnieuw
            }
        }, 4000);
        return () => clearTimeout(t);
    }, [files, projectId, sort]);

    const loadMore = async () => {
        if (nextOffset == null || loadingMore) return;
        setLoadingMore(true);
        try {
            // "Alles tonen": blijf pagina's ophalen tot alle bestanden geladen zijn
            let offset = nextOffset;
            let all = [...(files || [])];
            while (offset != null) {
                // eslint-disable-next-line no-await-in-loop
                const { data } = await api.get(`/projects/${projectId}/files`, {
                    params: { limit: 200, sort, offset },
                });
                all = [...all, ...(data.items || [])];
                offset = data.next_offset ?? null;
                setFiles([...all]);
                setNextOffset(offset);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoadingMore(false);
        }
    };

    const changeView = (v) => {
        setView(v);
        localStorage.setItem("sf_files_view", v);
    };

    const changeSort = (s) => {
        setSort(s);
        localStorage.setItem("sf_files_sort", s);
    };

    const filtered = (files || []).filter((f) => {
        if (!q.trim()) return true;
        const terms = q.trim().toLowerCase().split(/\s+/);
        const haystack = (
            f.original_name +
            " " +
            (f.ai_description || "") +
            " " +
            (f.ai_tags || []).join(" ")
        ).toLowerCase();
        return terms.every((t) => haystack.includes(t));
    });

    const onUpload = async (fileList) => {
        if (!fileList || fileList.length === 0) return;
        const filesArr = Array.from(fileList);
        setUploading(filesArr.length);
        setUploadProgress(filesArr.map((f) => ({ name: f.name, pct: 0, status: "pending" })));

        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        const doneSet = new Set();

        const uploadOne = async (file, i) => {
            const updateProgress = (patch) =>
                setUploadProgress((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i], ...patch };
                    return next;
                });
            const MAX_TRIES = 6;
            let init = null; // hergebruik dezelfde upload-sessie bij retries (geen dubbele records)
            for (let attempt = 1; attempt <= MAX_TRIES; attempt += 1) {
                try {
                    // 1) Init: get presigned PUT URL from backend
                    if (!init) {
                        updateProgress({ status: "initiating", pct: 0 });
                        const { data } = await api.post(
                            `/projects/${projectId}/files/direct-init`,
                            {
                                filename: file.name,
                                mime_type: file.type || "application/octet-stream",
                                size_bytes: file.size,
                            }
                        );
                        init = data;
                    }

                    // 2) PUT directly to Cloudflare R2 (bypasses ingress body limit)
                    updateProgress({ status: "uploading", pct: 0 });
                    let lastPct = 0;
                    await axios.put(init.upload_url, file, {
                        headers: { "Content-Type": init.mime_type },
                        timeout: 0, // no timeout — large videos can take a while
                        onUploadProgress: (evt) => {
                            if (!evt.total) return;
                            const pct = Math.round((evt.loaded / evt.total) * 100);
                            // throttle re-renders: alleen updaten per 5% (belangrijk bij 400 bestanden)
                            if (pct >= lastPct + 5 || pct === 100) {
                                lastPct = pct;
                                updateProgress({ pct });
                            }
                        },
                        transformRequest: [(d) => d], // prevent axios from JSON-stringifying File
                    });

                    // 3) Finalize: tell backend the upload is done -> thumbnail + AI
                    updateProgress({ status: "finalizing", pct: 100 });
                    await api.post(`/files/${init.file_id}/direct-finalize`);

                    updateProgress({ status: "done" });
                    doneSet.add(i);
                    setUploading((u) => Math.max(0, u - 1));
                    return true;
                } catch (e) {
                    // Sessie ongeldig (URL verlopen / record server-side verwijderd)? Dan verse init.
                    const st = e?.response?.status;
                    if (st === 400 || st === 403 || st === 404) init = null;
                    if (attempt < MAX_TRIES) {
                        // exponential backoff + jitter, capped at 20s
                        const wait = Math.min(20000, 1000 * 2 ** attempt) + Math.random() * 1000;
                        updateProgress({ status: "retrying", pct: 0, attempt: attempt + 1 });
                        await sleep(wait);
                    } else {
                        updateProgress({ status: "error", error: formatApiError(e) });
                    }
                }
            }
            return false;
        };

        // Worker pool: max 3 files in flight so the server never gets overloaded
        const runPool = async (indices) => {
            let ptr = 0;
            const workers = Array.from(
                { length: Math.min(3, indices.length) },
                async () => {
                    while (ptr < indices.length) {
                        const i = indices[ptr];
                        ptr += 1;
                        // eslint-disable-next-line no-await-in-loop
                        await uploadOne(filesArr[i], i);
                    }
                }
            );
            await Promise.all(workers);
        };

        // Blijf mislukte bestanden automatisch opnieuw proberen (max 4 rondes)
        const MAX_ROUNDS = 4;
        let pending = filesArr.map((_, i) => i);
        for (let round = 1; round <= MAX_ROUNDS && pending.length > 0; round += 1) {
            if (round > 1) {
                toast.info(
                    `${pending.length} bestand(en) mislukt — automatisch opnieuw proberen (ronde ${round})…`
                );
                await sleep(5000);
            }
            await runPool(pending);
            pending = pending.filter((i) => !doneSet.has(i));
        }

        const success = doneSet.size;
        const failed = filesArr.length - success;
        setUploading(0);
        if (success) {
            toast.success(`${success} van ${filesArr.length} bestand(en) geüpload — AI start automatisch.`);
        }
        if (failed) {
            toast.error(
                `${failed} bestand(en) definitief mislukt na ${MAX_ROUNDS} rondes — controleer je internetverbinding en probeer die opnieuw.`
            );
            // Auto-recover: files die wél in R2 zitten maar niet gefinaliseerd zijn
            if (isAdmin) {
                try {
                    const { data } = await api.post("/admin/recover-stranded-uploads");
                    if (data?.recovered > 0) {
                        toast.success(`${data.recovered} hangende upload(s) automatisch hersteld.`);
                    }
                } catch {
                    // stil: knop op dashboard blijft beschikbaar
                }
            }
        }
        loadFirst();
        // clear progress after a short delay so user sees the final state
        setTimeout(() => setUploadProgress([]), 6000);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        onUpload(Array.from(e.dataTransfer.files || []));
    };

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const applyBulkTags = async () => {
        const tags = bulkTag
            .split(",")
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean);
        if (!tags.length) {
            toast.error("Geef minstens 1 tag op (komma-gescheiden voor meerdere).");
            return;
        }
        setApplyingTags(true);
        try {
            await api.post("/files/bulk-tags", {
                file_ids: Array.from(selected),
                add_tags: tags,
            });
            toast.success(`Tags toegevoegd aan ${selected.size} bestand(en)`);
            setBulkTag("");
            clearSelection();
            loadFirst();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setApplyingTags(false);
        }
    };

    const bulkDownload = async () => {
        if (bulkBusy) return;
        setBulkBusy(true);
        setBulkZipProgress(null);
        try {
            // Robuust (WeTransfer-stijl): ZIP wordt in de opslag klaargezet,
            // daarna volgt een directe hervatbare downloadlink — niets in browsergeheugen.
            let { data: job } = await api.post("/files/zip", {
                file_ids: Array.from(selected),
            });
            let pollErrors = 0;
            const started = Date.now();
            while (job.status === "building") {
                setBulkZipProgress({ done: job.done_bytes, total: job.total_bytes });
                if (Date.now() - started > 60 * 60 * 1000) {
                    throw new Error("ZIP voorbereiden duurt te lang — probeer het opnieuw.");
                }
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 2000));
                try {
                    // eslint-disable-next-line no-await-in-loop
                    ({ data: job } = await api.get(`/zip-jobs/${job.job_id}`));
                    pollErrors = 0;
                } catch (e) {
                    pollErrors += 1;
                    if (pollErrors >= 8) throw e;
                }
            }
            if (job.status === "ready" && job.download_url) {
                const a = document.createElement("a");
                a.href = job.download_url;
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success("ZIP-download gestart — rechtstreeks uit de opslag.");
            } else {
                throw new Error(job.error || "ZIP maken mislukt — probeer het opnieuw.");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBulkBusy(false);
            setBulkZipProgress(null);
        }
    };

    const bulkApprove = async () => {
        try {
            const { data } = await api.post("/files/bulk-approve", {
                file_ids: Array.from(selected),
            });
            toast.success(`${data.approved} bestand(en) goedgekeurd`);
            clearSelection();
            loadFirst();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const bulkDelete = async () => {
        try {
            const { data } = await api.post("/files/bulk-delete", {
                file_ids: Array.from(selected),
            });
            toast.success(`${data.deleted} bestand(en) verwijderd`);
            clearSelection();
            loadFirst();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const selIdx = filtered.findIndex((f) => f.id === selectedId);

    return (
        <AppShell>
            <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1600px]" data-testid="project-page">
                {project && (
                    <Link
                        to={`/customers/${project.customer_id}`}
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-900 mb-6"
                        data-testid="back-to-customer"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        Terug naar {project.customer?.name || "klant"}
                    </Link>
                )}

                <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Project</div>
                        <h1 className="font-display text-3xl md:text-5xl tracking-tighter font-black leading-none break-words">
                            {project?.name || "…"}
                        </h1>
                        {project?.description && (
                            <p className="text-sm text-zinc-500 mt-2 max-w-xl">{project.description}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        {isAdmin && project && (
                            <ShareDialog
                                projectId={projectId}
                                project={project}
                                onChange={loadFirst}
                            />
                        )}
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0044FF] hover:bg-[#0033CC] text-white rounded-md text-sm font-medium"
                            data-testid="upload-button"
                        >
                            <Upload className="w-4 h-4" />
                            <span className="hidden sm:inline">Bestanden uploaden</span>
                            <span className="sm:hidden">Upload</span>
                        </button>
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => onUpload(Array.from(e.target.files || []))}
                        data-testid="upload-input"
                    />
                </div>

                {/* Search + sort + view toggle */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder='Zoek in dit project…'
                            className="w-full bg-white border border-zinc-300 rounded-md pl-12 pr-4 py-3 text-base shadow-sm h-auto"
                            data-testid="project-search-input"
                        />
                    </div>
                    <Select value={sort} onValueChange={changeSort}>
                        <SelectTrigger
                            className="w-auto min-w-[170px] bg-white border-zinc-300 rounded-md h-[50px] shadow-sm"
                            data-testid="sort-select"
                        >
                            <ArrowUpDown className="w-4 h-4 text-zinc-500 mr-1.5 shrink-0" />
                            <SelectValue placeholder="Sorteren" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="created_desc" data-testid="sort-created-desc">Nieuwste eerst</SelectItem>
                            <SelectItem value="created_asc" data-testid="sort-created-asc">Oudste eerst</SelectItem>
                            <SelectItem value="name_asc" data-testid="sort-name-asc">Naam A → Z</SelectItem>
                            <SelectItem value="name_desc" data-testid="sort-name-desc">Naam Z → A</SelectItem>
                            <SelectItem value="size_desc" data-testid="sort-size-desc">Grootste eerst</SelectItem>
                            <SelectItem value="size_asc" data-testid="sort-size-asc">Kleinste eerst</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex items-center bg-white border border-zinc-300 rounded-md shadow-sm h-[50px] p-1">
                        <button
                            type="button"
                            onClick={() => changeView("grid")}
                            title="Rasterweergave"
                            className={`h-full px-3 rounded-[4px] flex items-center justify-center transition-colors ${
                                view === "grid" ? "bg-[#0044FF] text-white" : "text-zinc-500 hover:text-zinc-900"
                            }`}
                            data-testid="view-grid-btn"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => changeView("list")}
                            title="Lijstweergave"
                            className={`h-full px-3 rounded-[4px] flex items-center justify-center transition-colors ${
                                view === "list" ? "bg-[#0044FF] text-white" : "text-zinc-500 hover:text-zinc-900"
                            }`}
                            data-testid="view-list-btn"
                        >
                            <ListIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Drop zone */}
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`mb-6 md:mb-8 border-2 border-dashed rounded-md p-5 md:p-8 text-center transition-all ${
                        dragOver
                            ? "border-[#0044FF] bg-[#0044FF]/5"
                            : "border-zinc-300 bg-white hover:border-zinc-400"
                    }`}
                    data-testid="dropzone"
                >
                    <Upload className="w-7 h-7 md:w-8 md:h-8 text-zinc-400 mx-auto mb-3" />
                    <div className="text-sm text-zinc-700 mb-1 font-medium">
                        <span className="hidden sm:inline">
                            Sleep bestanden hierheen of klik op "Bestanden uploaden"
                        </span>
                        <span className="sm:hidden">Tik op "Upload" om bestanden toe te voegen</span>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">
                        Foto's · Video's · PDF's · Documenten — AI beschrijft ze automatisch
                    </div>
                    {uploading > 0 && (
                        <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#0044FF]">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {uploading} bestand(en) uploaden…
                        </div>
                    )}
                    {uploadProgress.length > 0 && (
                        <div
                            className="mt-4 space-y-2 max-w-2xl mx-auto text-left"
                            data-testid="upload-progress-list"
                        >
                            {uploadProgress.map((u, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white border border-zinc-200 rounded-md p-2.5"
                                    data-testid={`upload-progress-${idx}`}
                                >
                                    <div className="flex items-center justify-between gap-2 text-xs font-mono mb-1.5">
                                        <span className="truncate min-w-0">{u.name}</span>
                                        <span
                                            title={u.status === "error" ? u.error : undefined}
                                            className={`shrink-0 max-w-[55%] truncate ${
                                                u.status === "error"
                                                    ? "text-red-600"
                                                    : u.status === "done"
                                                    ? "text-emerald-600"
                                                    : "text-zinc-600"
                                            }`}
                                        >
                                            {u.status === "pending" && "wachten…"}
                                            {u.status === "initiating" && "starten…"}
                                            {u.status === "uploading" && `${u.pct}%`}
                                            {u.status === "retrying" && `opnieuw (poging ${u.attempt || 2})…`}
                                            {u.status === "finalizing" && "afwerken…"}
                                            {u.status === "done" && "klaar"}
                                            {u.status === "error" && (u.error || "fout")}
                                        </span>
                                    </div>
                                    <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all ${
                                                u.status === "error"
                                                    ? "bg-red-500"
                                                    : u.status === "done"
                                                    ? "bg-emerald-500"
                                                    : "bg-[#0044FF]"
                                            }`}
                                            style={{
                                                width:
                                                    u.status === "done"
                                                        ? "100%"
                                                        : u.status === "error"
                                                        ? "100%"
                                                        : `${u.pct || 0}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {!files ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div
                                key={i}
                                className="aspect-square bg-white border border-zinc-200 rounded-md animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="border border-dashed border-zinc-300 rounded-md py-16 text-center bg-white">
                        <div className="font-display text-xl tracking-tight mb-2">
                            {q ? "Geen resultaten." : "Nog geen bestanden."}
                        </div>
                        <p className="text-sm text-zinc-500">
                            {q ? "Probeer andere zoektermen." : "Upload je eerste bestanden hierboven."}
                        </p>
                    </div>
                ) : (
                    <>
                        {view === "grid" ? (
                            <div
                                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4"
                                data-testid="file-grid"
                            >
                                {filtered.map((f) => (
                                    <FileCard
                                        key={f.id}
                                        file={f}
                                        onClick={() => setSelectedId(f.id)}
                                        selected={selected.has(f.id)}
                                        onToggleSelect={() => toggleSelect(f.id)}
                                        anySelected={selected.size > 0}
                                        onShare={() => setShareFileId(f.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div
                                className="bg-white border border-zinc-200 rounded-md overflow-hidden divide-y divide-zinc-100"
                                data-testid="file-list"
                            >
                                <div className="hidden md:grid grid-cols-[36px_56px_1fr_110px_150px_90px] gap-3 items-center px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                                    <span />
                                    <span />
                                    <span>Naam</span>
                                    <span>Grootte</span>
                                    <span>Datum</span>
                                    <span className="text-right">Acties</span>
                                </div>
                                {filtered.map((f) => (
                                    <FileListRow
                                        key={f.id}
                                        file={f}
                                        onClick={() => setSelectedId(f.id)}
                                        selected={selected.has(f.id)}
                                        onToggleSelect={() => toggleSelect(f.id)}
                                        anySelected={selected.size > 0}
                                        onShare={() => setShareFileId(f.id)}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {!q && nextOffset != null && (
                            <div className="flex flex-col items-center mt-8 gap-2">
                                <div className="text-xs font-mono text-zinc-500">
                                    {files.length} van {total} getoond
                                </div>
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outline"
                                    className="rounded-md"
                                    data-testid="load-more-button"
                                >
                                    {loadingMore ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Alles laden…
                                        </>
                                    ) : (
                                        <>Alles tonen ({total - files.length} meer)</>
                                    )}
                                </Button>
                            </div>
                        )}
                        {!q && nextOffset == null && files.length > PAGE_SIZE && (
                            <div className="text-center mt-8 text-xs font-mono text-zinc-500">
                                Alle {total} bestanden geladen.
                            </div>
                        )}
                    </>
                )}
            </div>

            <FileShareDialog
                fileId={shareFileId}
                open={!!shareFileId}
                onClose={() => setShareFileId(null)}
            />

            {selectedId && (
                <FileDetailDialog
                    fileId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onDeleted={loadFirst}
                    onUpdated={loadFirst}
                    onPrev={selIdx > 0 ? () => setSelectedId(filtered[selIdx - 1].id) : null}
                    onNext={
                        selIdx >= 0 && selIdx < filtered.length - 1
                            ? () => setSelectedId(filtered[selIdx + 1].id)
                            : null
                    }
                />
            )}

            {/* Bulk action bar */}
            {selected.size > 0 && (
                <div
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[#09090b] text-white rounded-md shadow-2xl px-4 py-3 flex items-center gap-3 flex-wrap max-w-[95vw] animate-fade-in"
                    data-testid="bulk-toolbar"
                >
                    <span className="text-sm font-medium" data-testid="bulk-count">
                        {selected.size} geselecteerd
                    </span>
                    <div className="h-5 w-px bg-white/20" />
                    <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-sm pl-2 py-0.5">
                        <input
                            type="text"
                            value={bulkTag}
                            onChange={(e) => setBulkTag(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    applyBulkTags();
                                }
                            }}
                            placeholder="tags (komma-gescheiden)"
                            className="bg-transparent text-sm outline-none placeholder:text-white/40 font-mono w-44 md:w-60"
                            data-testid="bulk-tag-input"
                        />
                        <button
                            onClick={applyBulkTags}
                            disabled={applyingTags || !bulkTag.trim()}
                            className="px-2 py-1 bg-[#0044FF] hover:bg-[#0033CC] rounded-sm text-xs font-medium disabled:opacity-50"
                            data-testid="bulk-tag-apply"
                        >
                            {applyingTags ? "Bezig…" : "Toevoegen"}
                        </button>
                    </div>
                    <button
                        onClick={bulkDownload}
                        disabled={bulkBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-sm text-xs font-medium disabled:opacity-50"
                        data-testid="bulk-download"
                    >
                        {bulkBusy ? (
                            <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span data-testid="bulk-zip-progress">
                                    {bulkZipProgress && bulkZipProgress.total > 0
                                        ? `ZIP… ${Math.min(100, Math.round((bulkZipProgress.done / bulkZipProgress.total) * 100))}%`
                                        : "ZIP…"}
                                </span>
                            </>
                        ) : (
                            <>
                                <Download className="w-3.5 h-3.5" />
                                ZIP
                            </>
                        )}
                    </button>
                    <button
                        onClick={bulkApprove}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-sm text-xs font-medium"
                        data-testid="bulk-approve"
                    >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Goedkeuren
                    </button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm text-xs font-medium"
                                data-testid="bulk-delete"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Verwijderen
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    {selected.size} bestand(en) verwijderen?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    De geselecteerde bestanden worden permanent verwijderd uit je
                                    opslag.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Annuleer</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={bulkDelete}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid="bulk-delete-confirm"
                                >
                                    Verwijderen
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    <button
                        onClick={clearSelection}
                        className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1"
                        data-testid="bulk-clear"
                    >
                        <X className="w-4 h-4" />
                        Wis selectie
                    </button>
                </div>
            )}
        </AppShell>
    );
}

function FileListRow({ file, onClick, selected, onToggleSelect, anySelected, onShare }) {
    const Icon = KIND_ICONS[file.kind] || FileQuestion;
    const isProcessing = file.status === "processing";
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => (anySelected ? onToggleSelect?.() : onClick?.())}
            onKeyDown={(e) => {
                if (e.key === "Enter") (anySelected ? onToggleSelect?.() : onClick?.());
            }}
            className={`grid grid-cols-[36px_56px_1fr_90px] md:grid-cols-[36px_56px_1fr_110px_150px_90px] gap-3 items-center px-3 py-2 cursor-pointer transition-colors ${
                selected ? "bg-[#0044FF]/5" : "hover:bg-zinc-50"
            }`}
            data-testid={`file-row-${file.id}`}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.();
                }}
                className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-all ${
                    selected
                        ? "bg-[#0044FF] border-[#0044FF] text-white"
                        : "bg-white border-zinc-300 text-transparent hover:border-[#0044FF]"
                }`}
                aria-label={selected ? "Deselecteer" : "Selecteer"}
                data-testid={`file-row-select-${file.id}`}
            >
                <Check className="w-3.5 h-3.5" />
            </button>
            <div className="w-14 h-14 rounded-sm bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0 relative">
                {file.thumb_url ? (
                    <img
                        src={file.thumb_url}
                        alt={file.original_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <Icon className="w-6 h-6 text-zinc-400" />
                )}
                {isProcessing && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                        <Loader2 className="w-4 h-4 text-[#0044FF] animate-spin" />
                    </div>
                )}
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-medium truncate">{file.original_name}</span>
                    {file.favorite && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                    {file.approved && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                    {file.comment_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-[#0044FF] shrink-0">
                            <MessageSquare className="w-3 h-3" />
                            {file.comment_count}
                        </span>
                    )}
                    {file.ai_tags?.length > 0 && (
                        <Sparkles className="w-3 h-3 text-[#0044FF] shrink-0" />
                    )}
                </div>
                {file.ai_description && (
                    <div className="text-xs text-zinc-500 truncate mt-0.5">{file.ai_description}</div>
                )}
                <div className="text-[11px] font-mono text-zinc-500 md:hidden mt-0.5">
                    {humanBytes(file.size_bytes)}
                </div>
            </div>
            <span className="hidden md:block text-xs font-mono text-zinc-600">
                {humanBytes(file.size_bytes)}
            </span>
            <span className="hidden md:block text-xs font-mono text-zinc-600">
                {file.created_at ? new Date(file.created_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" }) : "—"}
            </span>
            <div className="flex items-center justify-end gap-1">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShare?.();
                    }}
                    className="w-8 h-8 rounded-md text-zinc-500 hover:text-white hover:bg-[#0044FF] flex items-center justify-center transition-colors"
                    title="Deel bestand via publieke link"
                    data-testid={`file-row-share-${file.id}`}
                >
                    <Link2 className="w-4 h-4" />
                </button>
                {file.download_url && (
                    <a
                        href={file.download_url}
                        onClick={(e) => e.stopPropagation()}
                        className="w-8 h-8 rounded-md text-zinc-500 hover:text-white hover:bg-[#0044FF] flex items-center justify-center transition-colors"
                        title="Download origineel"
                        data-testid={`file-row-download-${file.id}`}
                    >
                        <Download className="w-4 h-4" />
                    </a>
                )}
            </div>
        </div>
    );
}

function FileCard({ file, onClick, selected, onToggleSelect, anySelected, onShare }) {
    const Icon = KIND_ICONS[file.kind] || FileQuestion;
    const isProcessing = file.status === "processing";

    return (
        <div
            className={`group relative bg-white border rounded-md overflow-hidden transition-all flex flex-col ${
                selected
                    ? "border-[#0044FF] ring-2 ring-[#0044FF]/30 shadow-md"
                    : "border-zinc-200 hover:border-[#0044FF] hover:shadow-md"
            }`}
            data-testid={`file-card-${file.id}`}
        >
            {/* Selection checkbox - always visible if any selected, otherwise on hover */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.();
                }}
                className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-sm border-2 flex items-center justify-center transition-all ${
                    selected
                        ? "bg-[#0044FF] border-[#0044FF] text-white opacity-100"
                        : "bg-white/90 border-zinc-300 text-transparent hover:border-[#0044FF] " +
                          (anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100")
                }`}
                aria-label={selected ? "Deselecteer" : "Selecteer"}
                data-testid={`file-select-${file.id}`}
            >
                <Check className="w-4 h-4" />
            </button>

            <div
                role="button"
                tabIndex={0}
                onClick={() => (anySelected ? onToggleSelect?.() : onClick?.())}
                onKeyDown={(e) => {
                    if (e.key === "Enter") (anySelected ? onToggleSelect?.() : onClick?.());
                }}
                className="text-left flex flex-col flex-1 min-w-0 cursor-pointer"
            >
                <div className="aspect-square bg-zinc-50 relative overflow-hidden flex items-center justify-center">
                    {/* Badges row (top-right) */}
                    <div className="absolute top-2 right-2 z-10 flex flex-col gap-1.5 items-end">
                        {file.favorite && (
                            <span
                                className="inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm shadow"
                                title="Favoriet"
                            >
                                <Star className="w-3 h-3 fill-white" />
                            </span>
                        )}
                        {file.approved && (
                            <span
                                className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm shadow"
                                title="Goedgekeurd"
                            >
                                <CheckCircle2 className="w-3 h-3" />
                            </span>
                        )}
                        {file.comment_count > 0 && (
                            <span
                                className="inline-flex items-center gap-1 bg-[#0044FF] text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm shadow"
                                title={`${file.comment_count} opmerkingen`}
                            >
                                <MessageSquare className="w-3 h-3" />
                                {file.comment_count}
                            </span>
                        )}
                    </div>
                    {file.thumb_url ? (
                        <img
                            src={file.thumb_url}
                            alt={file.original_name}
                            className="w-full h-full object-contain bg-zinc-50"
                            loading="lazy"
                            decoding="async"
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-2 text-zinc-400">
                            <Icon className="w-12 h-12" />
                            <span className="text-[10px] font-mono uppercase">{file.kind}</span>
                        </div>
                    )}
                    {isProcessing && (
                        <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-2 backdrop-blur-sm">
                            <Loader2 className="w-5 h-5 text-[#0044FF] animate-spin" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-700">
                                AI beschrijft…
                            </span>
                        </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/75 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="text-xs font-mono line-clamp-3">
                            {file.ai_description || file.original_name}
                        </div>
                    </div>
                    {file.download_url && (
                        <a
                            href={file.download_url}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute bottom-2 right-2 z-10 w-8 h-8 rounded-md bg-black/55 hover:bg-[#0044FF] text-white flex items-center justify-center backdrop-blur-sm transition-colors opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                            title="Download origineel"
                            aria-label={`Download ${file.original_name}`}
                            data-testid={`file-card-download-${file.id}`}
                        >
                            <Download className="w-4 h-4" />
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onShare?.();
                        }}
                        className="absolute bottom-2 right-12 z-10 w-8 h-8 rounded-md bg-black/55 hover:bg-[#0044FF] text-white flex items-center justify-center backdrop-blur-sm transition-colors opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                        title="Deel bestand via publieke link"
                        aria-label={`Deel ${file.original_name}`}
                        data-testid={`file-card-share-${file.id}`}
                    >
                        <Link2 className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-2.5 md:p-3">
                    <div className="text-sm font-medium truncate">{file.original_name}</div>
                    <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-mono text-zinc-500">
                            {humanBytes(file.size_bytes)}
                        </span>
                        {file.ai_tags?.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[#0044FF]">
                                <Sparkles className="w-3 h-3" />
                                AI
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
